import { Check, Loader2, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { toastApiError } from "@/api/errors";
import type { Role } from "@/api/types";
import { LoadingState } from "@/components/feedback/LoadingState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useAddTeamMember,
  useRemoveTeamMember,
  useTeamMembers,
  useUpdateTeamMemberRole,
} from "@/features/teams/hooks/useTeamMembers";
import { useUsers } from "@/features/users/hooks/useUsers";
import { Action } from "@/permissions/actions";
import { useCan } from "@/permissions/hooks";

interface TeamMembersDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamId: string;
  teamName: string;
}

const ROLES: Role[] = ["manager", "creator", "user"];

interface PickedUser {
  id: string;
  name: string;
  email: string;
  role: Role;
}

export function TeamMembersDialog({
  open,
  onOpenChange,
  teamId,
  teamName,
}: TeamMembersDialogProps) {
  const { t } = useTranslation("teams");
  const ROLE_LABELS: Record<Role, string> = {
    manager: t("roleManager"),
    creator: t("roleCreator"),
    user: t("roleUser"),
  };
  const { data: members, isLoading } = useTeamMembers(teamId);
  const { data: allUsers } = useUsers({ is_active: true });
  const add = useAddTeamMember(teamId);
  const updateRole = useUpdateTeamMemberRole(teamId);
  const remove = useRemoveTeamMember(teamId);
  const canManage = useCan(Action.TEAM_MEMBER_ADDED, { team_id: teamId });

  const [search, setSearch] = useState("");
  // Seleção acumulativa: vários usuários, cada um com seu papel (definido
  // DEPOIS de escolher quem entra).
  const [selected, setSelected] = useState<PickedUser[]>([]);

  const memberIds = new Set(members?.map((m) => m.user_id) ?? []);
  const selectedIds = new Set(selected.map((s) => s.id));
  const availableUsers =
    allUsers?.filter(
      (u) =>
        !memberIds.has(u.id) &&
        (search === "" ||
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase())),
    ) ?? [];

  function toggleUser(u: { id: string; name: string; email: string }) {
    setSelected((prev) =>
      prev.some((s) => s.id === u.id)
        ? prev.filter((s) => s.id !== u.id)
        : [...prev, { id: u.id, name: u.name, email: u.email, role: "user" }],
    );
  }

  function setSelectedRole(id: string, role: Role) {
    setSelected((prev) =>
      prev.map((s) => (s.id === id ? { ...s, role } : s)),
    );
  }

  async function handleAddSelected() {
    if (selected.length === 0) return;
    const results = await Promise.allSettled(
      selected.map((s) =>
        add.mutateAsync({ userId: s.id, role: s.role }),
      ),
    );
    const added = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - added;
    // Mantém na seleção apenas os que falharam (para retry).
    setSelected((prev) =>
      prev.filter((_, i) => results[i]?.status === "rejected"),
    );
    if (failed === 0) {
      toast.success(t("toasts.membersAdded", { count: added }));
      setSearch("");
    } else if (added === 0) {
      const firstRejected = results.find((r) => r.status === "rejected");
      toastApiError(
        firstRejected && firstRejected.status === "rejected"
          ? firstRejected.reason
          : undefined,
      );
    } else {
      toast.warning(t("toasts.membersAddedPartial", { added, failed }));
    }
  }

  async function handleRoleChange(userId: string, role: Role) {
    try {
      await updateRole.mutateAsync({ userId, role });
      toast.success(t("toasts.memberRoleUpdated"));
    } catch (err) {
      toastApiError(err);
    }
  }

  async function handleRemove(userId: string) {
    try {
      await remove.mutateAsync(userId);
      toast.success(t("toasts.memberRemoved"));
    } catch (err) {
      toastApiError(err);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("membersDialog.title", { name: teamName })}
          </DialogTitle>
          <DialogDescription>
            {t("membersDialog.description")}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? <LoadingState /> : null}

        {members && members.length > 0 ? (
          <ul className="divide-y rounded-md border">
            {members.map((m) => (
              <li
                key={m.user_id}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{m.user_name}</span>
                  <span className="text-xs text-muted-foreground">
                    {m.user_email}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    className="rounded-md border bg-background px-2 py-1 text-sm"
                    value={m.role}
                    onChange={(e) =>
                      handleRoleChange(m.user_id, e.target.value as Role)
                    }
                    disabled={!canManage || updateRole.isPending}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABELS[r]}
                      </option>
                    ))}
                  </select>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleRemove(m.user_id)}
                    disabled={!canManage || remove.isPending}
                    aria-label={t("membersDialog.removeAria")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : !isLoading ? (
          <p className="text-sm text-muted-foreground">
            {t("membersDialog.empty")}
          </p>
        ) : null}

        {canManage ? (
          <div className="space-y-3 border-t pt-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">
                {t("membersDialog.addSection")}
              </h3>
              {selected.length > 0 ? (
                <Badge variant="secondary">
                  {t("membersDialog.selectedCount", { count: selected.length })}
                </Badge>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-search">{t("membersDialog.search")}</Label>
              <Input
                id="user-search"
                placeholder={t("membersDialog.searchPlaceholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {availableUsers.length > 0 ? (
              <ul className="max-h-40 divide-y overflow-y-auto rounded-md border">
                {availableUsers.slice(0, 20).map((u) => {
                  const isSel = selectedIds.has(u.id);
                  return (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => toggleUser(u)}
                        aria-pressed={isSel}
                        className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-accent ${isSel ? "bg-accent" : ""}`}
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className={`flex h-4 w-4 items-center justify-center rounded border ${isSel ? "border-brand-500 bg-brand-500 text-white" : "border-line"}`}
                            aria-hidden
                          >
                            {isSel ? <Check className="h-3 w-3" /> : null}
                          </span>
                          {u.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {u.email}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("membersDialog.noUsersAvailable")}
              </p>
            )}

            {selected.length > 0 ? (
              <div className="space-y-2 rounded-md border p-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    {t("membersDialog.toAddSection")}
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelected([])}
                  >
                    {t("membersDialog.clearSelection")}
                  </Button>
                </div>
                <ul className="space-y-2">
                  {selected.map((s) => (
                    <li
                      key={s.id}
                      className="flex flex-wrap items-center justify-between gap-2"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{s.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {s.email}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <select
                          className="rounded-md border bg-background px-2 py-1 text-sm"
                          value={s.role}
                          onChange={(e) =>
                            setSelectedRole(s.id, e.target.value as Role)
                          }
                          aria-label={t("membersDialog.role")}
                        >
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {ROLE_LABELS[r]}
                            </option>
                          ))}
                        </select>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() =>
                            setSelected((prev) =>
                              prev.filter((p) => p.id !== s.id),
                            )
                          }
                          aria-label={t("membersDialog.removeFromSelectionAria")}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={handleAddSelected}
                  disabled={add.isPending}
                  className="w-full"
                >
                  {add.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : null}
                  {t("membersDialog.addSelected", { count: selected.length })}
                </Button>
              </div>
            ) : null}
          </div>
        ) : (
          <Badge variant="outline" className="self-start">
            {t("membersDialog.managePermissionHint")}
          </Badge>
        )}
      </DialogContent>
    </Dialog>
  );
}
